/**
 * "Move out a resident" guided flow — via the residents table kebab menu.
 *
 * Stays on the residents list rather than routing to the profile page: fewer
 * steps, no navigation, and it's the path most people already use.
 *
 * The confirmation is the shared MoveOutDialog. It used to be a native
 * window.confirm(), which couldn't be guided at all — a browser prompt is outside
 * the DOM, so it can be neither pointed at nor observed — so the list page was
 * changed to use the same styled dialog as the profile page.
 *
 * Pairs with moveInResident for verification: moving out and back in restores the
 * starting state, so this can be run repeatedly against one test resident.
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §6.3.
 */

export default {
    id: 'move-out-resident',
    title: 'Move out a resident',
    description: 'Remove a resident who has left the community.',

    minRoleWeight: 11,

    /* EHR-managed campuses move residents out by approving the EHR's task, so
       this is replaced there by moveOutResidentEhr. Exactly complementary gates,
       no weight-80 override — see moveInResident for why. */
    available: (ctx) => !ctx.ehrActive,

    startsAt: 'This will take you to the Residents page.',

    steps: [
        {
            id: 'open-row-menu',
            route: { path: '/community/residents', navigate: true },
            anchor: 'residents.row-actions',
            title: 'Open the resident’s row menu',
            body: 'Find the resident who is moving out and click the ⋮ menu at the end of their row.',
            placement: 'left',
            /* The menu item only renders while the menu is open, so its presence
               is exactly "the menu is open". */
            done: { type: 'dom', selector: '[data-help-id="row-actions.move-out"]' },
        },
        {
            id: 'choose-move-out',
            route: { path: '/community/residents', navigate: false },
            anchor: 'row-actions.move-out',
            title: 'Choose Move Out',
            body: 'Click Move Out. Nothing happens yet — the next screen asks you to confirm.',
            placement: 'left',
            /* Clicking Move Out closes the menu and opens the dialog in the same
               tick. The engine's regression guard makes completion win that race,
               so this doesn't bounce back to step 1 (HelpGuide context effect). */
            context: {
                selector: '[data-help-id="row-actions.move-out"]',
                contextStep: 'open-row-menu',
            },
            done: { type: 'dom', selector: '[data-help-id="move-out-dialog.confirm"]' },
        },
        {
            id: 'type-confirm',
            route: { path: '/community/residents', navigate: false },
            anchor: 'move-out-dialog.confirm-input',
            title: 'Type confirm to unlock',
            /* This is where the consequence gets spelled out. By the time someone
               reaches the button they have already decided; the typing step is the
               last moment the warning can actually change the outcome, so it says
               plainly what stops working rather than the softer "no longer
               active". */
            body: 'Check the name, then type “confirm” in the box. Moving out deactivates this resident’s pendant and any other assigned devices — they stop working and the system stops monitoring them.',
            placement: 'top',
            /* Cancelling the dialog sends the user back to re-open the row menu. */
            context: {
                selector: '[data-help-id="move-out-dialog.confirm"]',
                contextStep: 'open-row-menu',
            },
            /* "Validation passed" without any new detector type: the dialog
               disables its Move Out button until the typed word matches, so the
               button losing `disabled` IS the success signal. selectorVisible
               passes the selector straight to querySelectorAll, and the engine's
               MutationObserver already watches the `disabled` attribute
               (detectors.js:122-123), so this fires the moment the word matches. */
            done: {
                type: 'dom',
                selector: '[data-help-id="move-out-dialog.confirm"]:not([disabled])',
            },
        },
        {
            id: 'confirm',
            route: { path: '/community/residents', navigate: false },
            anchor: 'move-out-dialog.confirm',
            title: 'Complete the move out',
            body: 'Click Move Out. Nothing is deleted — they can be moved back in later from their profile.',
            placement: 'top',
            /* Deliberately the dialog's presence, NOT the enabled state. Clicking
               Move Out sets `saving`, which disables the button again while the
               write is in flight — an enabled-state context would read that as a
               back-out and regress mid-save. */
            context: {
                selector: '[data-help-id="move-out-dialog.confirm"]',
                contextStep: 'open-row-menu',
            },
            /* The list page calls moveOutResidents() → transType `resident_disable`
               (residentsApi.js:211). Note this is the same call the bulk move-out
               uses, so the detector can't tell a single row from a selection —
               acceptable, since it's the same operation either way. It is NOT
               `resident_disable_profile`; that only comes from the profile page. */
            done: { type: 'transaction', transTypes: ['resident_disable'] },
            timeoutMs: 15000,
        },
    ],

    success: {
        title: 'Resident moved out',
        body: 'They are now inactive. Their devices and wellness assignments are no longer active, and you can move them back in from their profile if they return.',
    },
};
